/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Text,
  Badge,
  VStack,
  HStack,
  Divider,
  Icon,
  Button,
  Center,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Td,
  Input,
  Flex,
} from "@chakra-ui/react";
import {
  FaWallet,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaFileInvoiceDollar,
  FaPrint,
  FaIdCard,
  FaCreditCard,
  FaClipboardList,
  FaClock,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import ResponsiveTable from "../../components/ResponsiveTable";
import UnifiedCard from "../../components/UnifiedCard";
import { formatNumber } from "../../../utils/formatNumber";

export default function Payments() {
  const { t } = useTranslation();
  const { isArabic, colors, dir, textAlign } = useTheme();

  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const printRef = useRef();
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        // Use tenantId=-1 to get all payments for all tenants (admin dashboard)
        // Or use specific tenantId to get payments for a specific tenant
        const tenantId = -1; // -1 means all tenants

        const res = await fetch(
          `${baseUrl}/GetTenantPayment?tenantId=${tenantId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        if (!res.ok) {
          throw new Error(`Failed to fetch payments: ${res.status}`);
        }

        const data = await res.json();
        if (Array.isArray(data)) {
          // Map data to include all needed fields
          setPayments(data.map((p) => ({
            ...p,
            PlanName: p.PlanName || p.PlanType || p.planName || p.planType || "",
            CreatedAt: p.CreatedAt || p.createdAt || p.StartDate || p.startDate || "",
          })));
        } else {
          console.warn("Unexpected API response format:", data);
          setPayments([]);
        }
      } catch (error) {
        console.error("Error fetching payments:", error);
        setPayments([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPayments();
  }, [baseUrl, isArabic]);

  const handlePrint = (p) => {
    const currentTime = new Date().toLocaleString("ar-JO", {
      dateStyle: "short",
      timeStyle: "medium",
    });

    const content = `
      <html>
        <head>
          <title>فاتورة ${p.TransactionId}</title>
          <style>
            body { font-family: 'Cairo', sans-serif; direction: ${dir}; text-align: ${textAlign}; padding: 20px; }
            .header { color: ${colors.mainColor}; font-size: 22px; font-weight: bold; margin-bottom: 10px; }
            .info { font-size: 16px; color: ${colors.textColor}; margin-bottom: 6px; }
            .highlight { color: ${colors.mainColor}; font-weight: bold; }
            .footer { margin-top: 20px; font-size: 14px; color: ${colors.subText}; text-align: center; }
          </style>
        </head>
        <body>
          <div class="header">فاتورة الدفع</div>
          <div class="info">المستخدم/المتجر: <span class="highlight">${p.UserName || p.TenantName || "—"}</span></div>
          <div class="info">رقم العملية: <span class="highlight">${p.TransactionId}</span></div>
          <div class="info"> المبلغ: <span class="highlight">
    ${formatNumber(p.TransactionAmount)} ${p.TransactionCurrency || "IQD"}</div>
          ${p.PlanName ? `<div class="info">نوع الخطة: <span class="highlight">${p.PlanName}</span></div>` : ''}
          <div class="info">الحالة: <span class="highlight">${getStatusLabel(p.TransactionStatus)}</span></div>
          <div class="info">طريقة الدفع: <span class="highlight">${p.PaymentMethodType || "—"}</span></div>
          <div class="info">تاريخ الدفع: <span class="highlight">${p.TransactionDate?.split("T")[0] || p.TransactionDate}</span></div>
          ${p.CreatedAt ? `<div class="info">تاريخ بدء العمل: <span class="highlight">${p.CreatedAt.split("T")[0] || p.CreatedAt}</span></div>` : ''}
          <div class="footer">تمت الطباعة بتاريخ ${currentTime}</div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  const handlePrintStatement = () => {
    const currentTime = new Date().toLocaleString("ar-JO", {
      dateStyle: "short",
      timeStyle: "medium",
    });

    // Filter payments by date range if provided
    let filteredPayments = payments;
    if (fromDate && toDate) {
      filteredPayments = payments.filter((p) => {
        const paymentDate = p.TransactionDate?.split("T")[0] || p.TransactionDate;
        return paymentDate >= fromDate && paymentDate <= toDate;
      });
    }

    // Calculate total
    const total = filteredPayments.reduce((sum, p) => {
      const amount = parseFloat(p.TransactionAmount) || 0;
      return sum + amount;
    }, 0);

    const content = `
      <html>
        <head>
          <title>كشف الدفعات</title>
          <style>
            body { font-family: 'Cairo', sans-serif; direction: ${dir}; text-align: ${textAlign}; padding: 20px; }
            .header { color: ${colors.mainColor}; font-size: 24px; font-weight: bold; margin-bottom: 20px; text-align: center; }
            .period { font-size: 16px; color: ${colors.textColor}; margin-bottom: 15px; text-align: center; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th { background-color: ${colors.mainColor}; color: white; padding: 10px; text-align: ${textAlign}; border: 1px solid #ddd; }
            td { padding: 8px; border: 1px solid #ddd; }
            .total { font-size: 20px; font-weight: bold; color: ${colors.mainColor}; text-align: center; margin-top: 20px; padding: 15px; background-color: ${colors.hoverBg}; }
            .footer { margin-top: 20px; font-size: 14px; color: ${colors.subText}; text-align: center; }
            @media print { body { padding: 10px; } }
          </style>
        </head>
        <body>
          <div class="header">${isArabic ? "كشف جميع الدفعات" : "All Payments Statement"}</div>
          ${fromDate && toDate ? `<div class="period">${isArabic ? "الفترة: من " : "Period: From "}${fromDate} ${isArabic ? "إلى " : "To "}${toDate}</div>` : `<div class="period">${isArabic ? "جميع الدفعات" : "All Payments"}</div>`}
          <table>
            <thead>
              <tr>
                <th>${isArabic ? "رقم العملية" : "Transaction ID"}</th>
                <th>${isArabic ? "المستخدم/المتجر" : "User/Store"}</th>
                <th>${isArabic ? "المبلغ" : "Amount"}</th>
                <th>${isArabic ? "نوع الخطة" : "Plan Type"}</th>
                <th>${isArabic ? "الحالة" : "Status"}</th>
                <th>${isArabic ? "تاريخ الدفع" : "Payment Date"}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredPayments.map((p) => `
                <tr>
                  <td>#${p.TransactionId}</td>
                  <td>${p.UserName || p.TenantName || "—"}</td>
                  <td>${formatNumber(p.TransactionAmount)}
 ${p.TransactionCurrency || "IQD"}</td>
                  <td>${p.PlanName || "—"}</td>
                  <td>${getStatusLabel(p.TransactionStatus)}</td>
                  <td>${p.TransactionDate?.split("T")[0] || p.TransactionDate}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="total">
           المبلغ: <span class="highlight">
  ${isArabic ? "المجموع النهائي: " : "Total: "} ${formatNumber(total)} IQD
          </div>
          <div class="footer">${isArabic ? "تمت الطباعة بتاريخ " : "Printed on "}${currentTime}</div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.print();
  };

  if (loading)
    return (
      <Center minH="80vh">
        <Spinner size="xl" color={colors.mainColor} />
      </Center>
    );

  const getStatusLabel = (status) => {
    if (isArabic) {
      if (status === "paid") return "مدفوع";
      if (status === "failed") return "فشل الدفع";
      if (status === "waiting") return "قيد الانتظار";
      return status;
    }
    return status;
  };

  return (
    <PageContainer title={isArabic ? "المدفوعات والاشتراكات" : "Payments & Subscriptions"}>
      {payments.length > 0 && (
        <UnifiedCard mb={6} hoverable={false}>
          <VStack spacing={4} align="stretch">
            <Text fontWeight="bold" color={colors.mainColor} fontSize="lg">
              {isArabic ? "طباعة كشف الدفعات" : "Print Payment Statement"}
            </Text>
            <Flex gap={3} flexWrap="wrap" align="flex-end">
              <Box flex="1" minW="150px">
                <Text mb={2} fontSize="sm" color={colors.textColor}>
                  {isArabic ? "من تاريخ" : "From Date"}
                </Text>
                <Input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  bg={colors.inputBg}
                  color={colors.textColor}
                  borderColor={colors.inputBorder}
                  _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                  _hover={{ borderColor: colors.inputFocus }}
                  _dark={{ bg: colors.inputBg, color: colors.textColor }}
                />
              </Box>
              <Box flex="1" minW="150px">
                <Text mb={2} fontSize="sm" color={colors.textColor}>
                  {isArabic ? "إلى تاريخ" : "To Date"}
                </Text>
                <Input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  bg={colors.inputBg}
                  color={colors.textColor}
                  borderColor={colors.inputBorder}
                  _focus={{ borderColor: colors.inputFocus, boxShadow: `0 0 0 1px ${colors.inputFocus}` }}
                  _hover={{ borderColor: colors.inputFocus }}
                  _dark={{ bg: colors.inputBg, color: colors.textColor }}
                />
              </Box>
              <Button
                leftIcon={<FaPrint />}
                bg={colors.mainColor}
                color={colors.buttonText}
                _hover={{ bg: colors.mainColorHover }}
                onClick={handlePrintStatement}
              >
                {isArabic ? "طباعة الكشف" : "Print Statement"}
              </Button>
            </Flex>
          </VStack>
        </UnifiedCard>
      )}
      {payments.length === 0 ? (
        <Center py={10}>
          <Text color={colors.subText}>
            {isArabic ? "لا توجد عمليات دفع لعرضها" : "No payments found"}
          </Text>
        </Center>
      ) : (
        <ResponsiveTable
          columns={[
            { header: isArabic ? "المستخدم/المتجر" : "User/Store", sortKey: "UserName" },
            { header: isArabic ? "رقم العملية" : "Transaction ID", sortKey: "TransactionId" },
            { header: isArabic ? "المبلغ" : "Amount", sortKey: "TransactionAmount" },
            { header: isArabic ? "نوع الخطة" : "Plan Type", sortKey: "PlanName" },
            { header: isArabic ? "طريقة الدفع" : "Payment Method", sortKey: "PaymentMethodType" },
            { header: isArabic ? "الحالة" : "Status", sortKey: "TransactionStatus" },
            { header: isArabic ? "تاريخ الدفع" : "Payment Date", sortKey: "TransactionDate" },
            { header: isArabic ? "تاريخ بدء العمل" : "Start Date", sortKey: "CreatedAt" },
            { header: isArabic ? "الإجراءات" : "Actions" },
          ]}
          data={payments}
          searchable={true}
          sortable={true}
          filterOptions={{
            field: "TransactionStatus",
            options: [
              { value: "", label: isArabic ? "الكل" : "All" },
              { value: "paid", label: isArabic ? "مدفوع" : "Paid" },
              { value: "failed", label: isArabic ? "فشل" : "Failed" },
              { value: "waiting", label: isArabic ? "قيد الانتظار" : "Waiting" },
            ],
          }}
          renderRow={(p) => (
            <>
              <Td color={colors.textColor} fontWeight="medium">
                <HStack>
                  <Icon as={FaWallet} color={colors.mainColor} />
                  <Text>{p.UserName || p.TenantName || "—"}</Text>
                </HStack>
              </Td>
              <Td color={colors.textColor} fontWeight="semibold" textAlign="center">
                #{p.TransactionId}
              </Td>
              <Td fontWeight="bold" color={colors.mainColor} textAlign="center">
                {formatNumber(p.TransactionAmount)}
                {p.TransactionCurrency || "IQD"}
              </Td>
              <Td color={colors.textColor}>
                {p.PlanName ? (
                  <HStack>
                    <Icon as={FaClipboardList} color={colors.mainColor} />
                    <Text>{p.PlanName}</Text>
                  </HStack>
                ) : (
                  <Text color={colors.subText}>—</Text>
                )}
              </Td>
              <Td color={colors.textColor}>
                <HStack>
                  <Icon as={FaCreditCard} color={colors.mainColor} />
                  <Text>{p.PaymentMethodType || "—"}</Text>
                </HStack>
              </Td>
              <Td>
                <Badge
                  bg={
                    p.TransactionStatus === "paid"
                      ? colors.badgeSuccess
                      : p.TransactionStatus === "failed"
                        ? colors.badgeError
                        : colors.badgeWarning
                  }
                  color={
                    p.TransactionStatus === "paid"
                      ? colors.badgeSuccessText
                      : p.TransactionStatus === "failed"
                        ? colors.badgeErrorText
                        : "yellow.800"
                  }
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontWeight="semibold"
                >
                  {getStatusLabel(p.TransactionStatus)}
                </Badge>
              </Td>
              <Td color={colors.textColor} whiteSpace="nowrap" textAlign={textAlign}>
                {p.TransactionDate?.split("T")[0] || p.TransactionDate}
              </Td>
              <Td color={colors.textColor} whiteSpace="nowrap" textAlign={textAlign}>
                {p.CreatedAt ? (
                  <HStack>
                    <Icon as={FaClock} color={colors.mainColor} />
                    <Text>{p.CreatedAt.split("T")[0] || p.CreatedAt}</Text>
                  </HStack>
                ) : (
                  <Text color={colors.subText}>—</Text>
                )}
              </Td>
              <Td>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    leftIcon={<FaFileInvoiceDollar />}
                    bg={colors.buttonBg}
                    color={colors.buttonText}
                    _hover={{ bg: colors.buttonHover }}
                    onClick={() => {
                      setSelectedPayment(p);
                      onOpen();
                    }}
                  >
                    {isArabic ? "عرض" : "View"}
                  </Button>
                  <Button
                    size="sm"
                    leftIcon={<FaPrint />}
                    bg={colors.mainColor}
                    color={colors.buttonText}
                    _hover={{ bg: colors.mainColorHover }}
                    onClick={() => handlePrint(p)}
                  >
                    {isArabic ? "طباعة" : "Print"}
                  </Button>
                </HStack>
              </Td>
            </>
          )}
          renderCard={(p) => (
            <>
              <HStack justify="space-between" mb={3}>
                <HStack>
                  <Icon as={FaWallet} color={colors.mainColor} boxSize={5} />
                  <Text fontWeight="bold" fontSize="lg" color={colors.textColor}>
                    {p.UserName || p.TenantName || "—"}
                  </Text>
                </HStack>
                <Badge
                  bg={
                    p.TransactionStatus === "paid"
                      ? colors.badgeSuccess
                      : p.TransactionStatus === "failed"
                        ? colors.badgeError
                        : colors.badgeWarning
                  }
                  color={
                    p.TransactionStatus === "paid"
                      ? colors.badgeSuccessText
                      : p.TransactionStatus === "failed"
                        ? colors.badgeErrorText
                        : "yellow.800"
                  }
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontWeight="semibold"
                >
                  {getStatusLabel(p.TransactionStatus)}
                </Badge>
              </HStack>

              <Divider my={3} borderColor={colors.dividerColor} />

              <VStack align="start" spacing={2} mb={4}>
                <Text color={colors.textColor} fontSize="sm">
                  <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                    {isArabic ? "رقم العملية: " : "Transaction ID: "}
                  </Text>
                  #{p.TransactionId}
                </Text>
                <Text color={colors.textColor} fontSize="sm">
                  <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                    {isArabic ? "المبلغ: " : "Amount: "}
                  </Text>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {formatNumber(p.TransactionAmount)}
                    {p.TransactionCurrency || "IQD"}
                  </Text>
                </Text>
                <HStack>
                  <Icon as={FaCreditCard} color={colors.mainColor} />
                  <Text color={colors.textColor} fontSize="sm">
                    <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                      {isArabic ? "طريقة الدفع: " : "Payment Method: "}
                    </Text>
                    {p.PaymentMethodType || "—"}
                  </Text>
                </HStack>
                <HStack>
                  <Icon as={FaCalendarAlt} color={colors.mainColor} />
                  <Text color={colors.textColor} fontSize="sm">
                    <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                      {isArabic ? "التاريخ: " : "Date: "}
                    </Text>
                    {p.TransactionDate?.split("T")[0] || p.TransactionDate}
                  </Text>
                </HStack>
                {p.PlanName && (
                  <Text color={colors.textColor} fontSize="sm">
                    <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                      {isArabic ? "الخطة: " : "Plan: "}
                    </Text>
                    {p.PlanName}
                  </Text>
                )}
                {p.CreatedAt && (
                  <HStack>
                    <Icon as={FaClock} color={colors.mainColor} />
                    <Text color={colors.textColor} fontSize="sm">
                      <Text as="span" fontWeight="semibold" color={colors.mainColor}>
                        {isArabic ? "تاريخ بدء العمل: " : "Start Date: "}
                      </Text>
                      {p.CreatedAt.split("T")[0] || p.CreatedAt}
                    </Text>
                  </HStack>
                )}
              </VStack>

              <HStack spacing={2}>
                <Button
                  size="sm"
                  leftIcon={<FaFileInvoiceDollar />}
                  bg={colors.buttonBg}
                  color={colors.buttonText}
                  _hover={{ bg: colors.buttonHover }}
                  flex={1}
                  onClick={() => {
                    setSelectedPayment(p);
                    onOpen();
                  }}
                >
                  {isArabic ? "عرض الفاتورة" : "View Invoice"}
                </Button>
                <Button
                  size="sm"
                  leftIcon={<FaPrint />}
                  bg={colors.mainColor}
                  color={colors.buttonText}
                  _hover={{ bg: colors.mainColorHover }}
                  flex={1}
                  onClick={() => handlePrint(p)}
                >
                  {isArabic ? "طباعة" : "Print"}
                </Button>
              </HStack>
            </>
          )}
        />
      )}

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent dir={dir} ref={printRef}>
          <ModalHeader color={colors.mainColor}>{isArabic ? "تفاصيل الفاتورة" : "Invoice Details"}</ModalHeader>
          <ModalBody>
            {selectedPayment && (
              <VStack align="start" spacing={3}>
                <Text color={colors.textColor}>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {isArabic ? "المستخدم/المتجر: " : "User/Store: "}
                  </Text>
                  {selectedPayment.UserName || selectedPayment.TenantName || "—"}
                </Text>
                <Text color={colors.textColor}>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {isArabic ? "رقم العملية: " : "Transaction ID: "}
                  </Text>
                  #{selectedPayment.TransactionId}
                </Text>
                <Text color={colors.textColor}>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {isArabic ? "المبلغ: " : "Amount: "}
                  </Text>
                  {selectedPayment.TransactionAmount} {selectedPayment.TransactionCurrency || "IQD"}
                </Text>
                {selectedPayment.PlanName && (
                  <Text color={colors.textColor}>
                    <Text as="span" fontWeight="bold" color={colors.mainColor}>
                      {isArabic ? "نوع الخطة: " : "Plan Type: "}
                    </Text>
                    {selectedPayment.PlanName}
                  </Text>
                )}
                <Text color={colors.textColor}>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {isArabic ? "الحالة: " : "Status: "}
                  </Text>
                  {getStatusLabel(selectedPayment.TransactionStatus)}
                </Text>
                <Text color={colors.textColor}>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {isArabic ? "طريقة الدفع: " : "Payment Method: "}
                  </Text>
                  {selectedPayment.PaymentMethodType || "—"}
                </Text>
                <Text color={colors.textColor}>
                  <Text as="span" fontWeight="bold" color={colors.mainColor}>
                    {isArabic ? "تاريخ الدفع: " : "Payment Date: "}
                  </Text>
                  {selectedPayment.TransactionDate?.split("T")[0] || selectedPayment.TransactionDate}
                </Text>
                {selectedPayment.CreatedAt && (
                  <Text color={colors.textColor}>
                    <Text as="span" fontWeight="bold" color={colors.mainColor}>
                      {isArabic ? "تاريخ بدء العمل: " : "Start Date: "}
                    </Text>
                    {selectedPayment.CreatedAt.split("T")[0] || selectedPayment.CreatedAt}
                  </Text>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} colorScheme="purple">
              إغلاق
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageContainer>
  );
}
